import {
  type Resource,
  type Action,
  type Group,
  type GroupResourceActionMapping,
  type UpdateGroupParams,
} from "@/api/groupService";
import { usePermissions } from "@/hooks/usePermissions";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Paper,
  CircularProgress,
} from "@mui/material";
import { useEffect, useState } from "react";

type MappingObject = {
  [groupId: string]: {
    [resourceId: string]: {
      [actionId: string]: boolean;
    };
  };
};

interface AuthorizationManageTableProps {
  resources?: Resource[];
  actions?: Action[];
  groups?: Group[];
  groupResourceActionMapping?: GroupResourceActionMapping[];
  setChangedPermissions?: (
    updateFn: (prev: UpdateGroupParams) => UpdateGroupParams,
  ) => void;
  isLoading?: boolean;
  isSaving?: boolean;
}

const AuthorizationManageTable = ({
  resources,
  actions,
  groups,
  groupResourceActionMapping,
  setChangedPermissions,
  isLoading = false,
  isSaving = false,
}: AuthorizationManageTableProps) => {
  const canEdit = usePermissions().canEdit("role");
  const [mappingObject, setMappingObject] = useState<MappingObject>({});

  useEffect(() => {
    if (!groupResourceActionMapping) {
      return;
    }

    const newMappingObject = groupResourceActionMapping.reduce(
      (acc, groupMapping) => {
        const groupId = groupMapping.group.id;
        acc[groupId] = {};

        groupMapping.resources.forEach((resourceMapping) => {
          const resourceId = resourceMapping.resource.id;
          acc[groupId][resourceId] = {};

          resourceMapping.actions.forEach((action) => {
            acc[groupId][resourceId][action.id] = action.allowed;
          });
        });

        return acc;
      },
      {} as MappingObject,
    );

    setMappingObject(newMappingObject);
  }, [groupResourceActionMapping]);

  const changePermissions = (
    groupId: number,
    resourceId: number,
    actionId: number,
    allowed: boolean,
  ) => {
    if (setChangedPermissions) {
      setChangedPermissions((prev: UpdateGroupParams) => {
        const filteredPermissions = prev.permissions.filter(
          (permission) =>
            !(
              permission.group_id === groupId &&
              permission.resource_id === resourceId &&
              permission.action_id === actionId
            ),
        );

        return {
          permissions: [
            ...filteredPermissions,
            {
              group_id: groupId,
              resource_id: resourceId,
              action_id: actionId,
              allowed: allowed,
            },
          ],
        };
      });
    }
  };

  const handleCheckboxChange = (
    groupId: number,
    resourceId: number,
    actionId: number,
  ) => {
    // If edit is checked -> check view too
    const actionName = actions?.find(
      (action) => action.id === actionId,
    )?.action_key;
    if (
      actionName === "edit" &&
      !mappingObject[groupId]?.[resourceId]?.[actionId]
    ) {
      const viewAction = actions?.find(
        (action) => action.action_key === "view",
      );
      if (viewAction) {
        changePermissions(groupId, resourceId, viewAction.id, true);
        setMappingObject((prev) => ({
          ...prev,
          [groupId]: {
            ...prev[groupId],
            [resourceId]: {
              ...prev[groupId][resourceId],
              [viewAction.id]: true,
            },
          },
        }));
      }
    }

    // If view is unchecked -> uncheck edit too
    if (
      actionName === "view" &&
      mappingObject[groupId]?.[resourceId]?.[actionId]
    ) {
      const editAction = actions?.find(
        (action) => action.action_key === "edit",
      );
      if (editAction) {
        changePermissions(groupId, resourceId, editAction.id, false);

        setMappingObject((prev) => ({
          ...prev,
          [groupId]: {
            ...prev[groupId],
            [resourceId]: {
              ...prev[groupId][resourceId],
              [editAction.id]: false,
            },
          },
        }));
      }
    }

    changePermissions(
      groupId,
      resourceId,
      actionId,
      !mappingObject[groupId]?.[resourceId]?.[actionId],
    );

    setMappingObject((prev) => ({
      ...prev,
      [groupId]: {
        ...prev[groupId],
        [resourceId]: {
          ...prev[groupId][resourceId],
          [actionId]: !prev[groupId][resourceId][actionId],
        },
      },
    }));
  };

  return (
    <Box sx={{ position: "relative" }}>
      {isSaving && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(255, 255, 255, 0.7)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <CircularProgress size={60} thickness={4} color="primary" />
        </Box>
      )}
      <TableContainer
        component={Paper}
        sx={{
          maxHeight: "70vh",
          boxShadow: "none",
          border: "1px solid #e0e0e0",
          borderRadius: 2,
          overflowX: "auto",
        }}
      >
        <Table
          stickyHeader
          sx={{
            borderCollapse: "separate",
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell
                rowSpan={2}
                sx={{
                  minWidth: 200,
                  fontWeight: "bold",
                  position: "sticky",
                  left: 0,
                  top: 0,
                  zIndex: 4,
                  borderRight: "1px solid #e0e0e0",
                  fontSize: "1rem",
                }}
              >
                สิทธิ์การใช้งาน
              </TableCell>

              {groups?.map((group) => (
                <TableCell
                  key={group.id}
                  colSpan={actions?.length || 1}
                  align="center"
                  sx={{
                    position: "sticky",
                    top: 0,
                    zIndex: 3,
                    height: 80,
                    borderBottom: "1px solid #e0e0e0",
                    padding: "8px 4px",
                  }}
                >
                  <Box
                    sx={{
                      display: "inline-block",
                      //transform: "rotate(-45deg)",
                      //transformOrigin: "center center",
                      whiteSpace: "nowrap",
                      fontSize: "0.9rem",
                      fontWeight: "bold",
                    }}
                  >
                    {group.display_name}
                  </Box>
                </TableCell>
              ))}
            </TableRow>
            <TableRow>
              {groups?.map((group) =>
                actions?.map((action) => (
                  <TableCell
                    key={`${group.id}-${action.id}`}
                    align="center"
                    sx={{
                      position: "sticky",
                      top: 80,
                      zIndex: 2,
                      fontWeight: "bold",
                      fontSize: "0.9rem",
                      borderBottom: "1px solid #e0e0e0",
                    }}
                  >
                    {action.display_name}
                  </TableCell>
                )),
              )}
            </TableRow>
          </TableHead>

          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={(groups?.length || 0) * (actions?.length || 0) + 1}
                  align="center"
                  sx={{ py: 5 }}
                >
                  <Box
                    sx={{
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      outline: "none",
                    }}
                  >
                    <CircularProgress size={60} thickness={4} color="primary" />
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              resources?.map((resource) => (
                <TableRow key={resource.id}>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      position: "sticky",
                      left: 0,
                      zIndex: 1,
                      borderRight: "1px solid #e0e0e0",
                      fontSize: "1rem",
                      backgroundColor: "white",
                    }}
                  >
                    {resource.display_name}
                  </TableCell>

                  {groups?.map((group, index) =>
                    actions?.map((action) => {
                      const isAllowed =
                        mappingObject[group.id]?.[resource.id]?.[action.id] ??
                        false;

                      return (
                        <TableCell
                          align="center"
                          key={`${group.id}-${resource.id}-${action.id}`}
                          sx={{
                            backgroundColor:
                              index % 2 === 0
                                ? "rgba(25, 118, 210, 0.05)"
                                : "transparent",
                          }}
                        >
                          <Box display="flex" justifyContent="center">
                            <Checkbox
                              size="medium"
                              checked={isAllowed}
                              onChange={() =>
                                handleCheckboxChange(
                                  group.id,
                                  resource.id,
                                  action.id,
                                )
                              }
                              disabled={!canEdit}
                            />
                          </Box>
                        </TableCell>
                      );
                    }),
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default AuthorizationManageTable;
